import { createContext } from "react";
import { useQuery } from "react-query";
import { appearance } from "#/api/queries/appearance";
import { buildInfo } from "#/api/queries/buildInfo";
import { entitlements } from "#/api/queries/entitlements";
import { experiments } from "#/api/queries/experiments";
import { organizations } from "#/api/queries/organizations";
import type {
	AppearanceConfig,
	BuildInfoResponse,
	Entitlements,
	Experiment,
	Organization,
} from "#/api/typesGenerated";
import { ErrorAlert } from "#/components/Alert/ErrorAlert";
import { Loader } from "#/components/Loader/Loader";
import { useAuthenticated } from "#/hooks/useAuthenticated";
import { useEmbeddedMetadata } from "#/hooks/useEmbeddedMetadata";
import { canViewAnyOrganization } from "#/modules/permissions";
import { selectFeatureVisibility } from "./entitlements";

export type DashboardValue = {
	entitlements: Entitlements;
	experiments: Experiment[];
	appearance: AppearanceConfig;
	buildInfo: BuildInfoResponse;
	organizations: readonly Organization[];
	showOrganizations: boolean;
	canViewOrganizationSettings: boolean;
};

export const DashboardContext = createContext<DashboardValue | undefined>(
	undefined,
);

export const DashboardProvider: React.FC<React.PropsWithChildren> = ({
	children,
}) => {
	const { metadata } = useEmbeddedMetadata();
	const { user, permissions } = useAuthenticated();
	const entitlementsQuery = useQuery(entitlements(metadata.entitlements));
	const experimentsQuery = useQuery(experiments(user.id, metadata));
	const appearanceQuery = useQuery(appearance(metadata.appearance));
	const buildInfoQuery = useQuery(buildInfo(metadata["build-info"]));
	const organizationsQuery = useQuery(organizations(metadata.organizations));

	const error =
		entitlementsQuery.error ||
		appearanceQuery.error ||
		// Experiments refetch in the background; keep the last list when a
		// refetch fails instead of replacing the dashboard with an error.
		(!experimentsQuery.data && experimentsQuery.error) ||
		buildInfoQuery.error ||
		organizationsQuery.error;

	if (error) {
		return <ErrorAlert error={error} />;
	}

	const isLoading =
		!entitlementsQuery.data ||
		!appearanceQuery.data ||
		!experimentsQuery.data ||
		!buildInfoQuery.data ||
		!organizationsQuery.data;

	if (isLoading) {
		return <Loader fullscreen />;
	}

	const hasMultipleOrganizations = organizationsQuery.data.length > 1;
	const organizationsEnabled = selectFeatureVisibility(
		entitlementsQuery.data,
	).multiple_organizations;
	const showOrganizations = hasMultipleOrganizations || organizationsEnabled;

	return (
		<DashboardContext.Provider
			value={{
				entitlements: entitlementsQuery.data,
				experiments: experimentsQuery.data,
				appearance: appearanceQuery.data,
				buildInfo: buildInfoQuery.data,
				organizations: organizationsQuery.data,
				showOrganizations,
				canViewOrganizationSettings:
					showOrganizations && canViewAnyOrganization(permissions),
			}}
		>
			{children}
		</DashboardContext.Provider>
	);
};
